import type { RailBranch } from "@/data/rail-branches";
import { MS_PER_PIXEL } from "@/lib/timeline/book/BookLayout";
import { markerPackStep } from "../../marker/MarkerMetrics/metrics";

/** Vertical distance between parallel location lines, in world CSS px. */
export const RAIL_LANE_PITCH = 720;

/** Horizontal length of a fork or rejoin curve, in world CSS px. */
export const RAIL_CURVE_PX = 420;

export function railLaneOffsetY(lane: number, offsets?: Record<string, number>): number {
  return offsets?.[lane] ?? lane * RAIL_LANE_PITCH;
}

export function railCurveMs(): number {
  return RAIL_CURVE_PX * MS_PER_PIXEL;
}

/** A short branch bends sooner, so its fork and rejoin curves cannot overlap. */
export function branchCurveMs(branch: { startMs: number; endMs: number }): number {
  return Math.min(railCurveMs(), Math.max(1, branch.endMs - branch.startMs) / 3);
}

/** When the fork curve runs: inside the branch, or before it when it leads in. */
export function forkCurveSpan(branch: RailBranch): { startMs: number; endMs: number } {
  const curveMs = branchCurveMs(branch);
  return branch.leadIn === true
    ? { startMs: branch.startMs - curveMs, endMs: branch.startMs }
    : { startMs: branch.startMs, endMs: branch.startMs + curveMs };
}

/** When the rejoin curve runs: inside the branch, or after it when it leads out. */
export function joinCurveSpan(branch: RailBranch): { startMs: number; endMs: number } {
  const curveMs = branchCurveMs(branch);
  return branch.leadOut === true
    ? { startMs: branch.endMs, endMs: branch.endMs + curveMs }
    : { startMs: branch.endMs - curveMs, endMs: branch.endMs };
}

export function openRailLanes(
  branches: readonly RailBranch[],
  playheadMs: number,
): number {
  const lanes = new Set<number>();
  for (const branch of branches) {
    if (playheadMs >= branch.startMs && playheadMs < branch.endMs) {
      lanes.add(branch.lane);
    }
  }
  return lanes.size;
}

/** When the number of lines last changed, if that was inside the curve. */
export function recentLaneChangeMs(
  branches: readonly RailBranch[],
  playheadMs: number,
  curveMs: number,
): number | undefined {
  let latest: number | undefined;
  for (const branch of branches) {
    for (const at of [branch.startMs, branch.endMs]) {
      if (playheadMs < at || playheadMs - at >= curveMs) {
        continue;
      }
      const before = openRailLanes(branches, at - 1);
      const after = openRailLanes(branches, at);
      if (before < 1 || before === after) {
        continue;
      }
      if (latest === undefined || at > latest) {
        latest = at;
      }
    }
  }
  return latest;
}

/** Sit on the line, side by side, once a second line is open. */
export function markerLineOffset(
  index: number,
  count: number,
): { dx: number; dy: number } {
  if (count <= 1) {
    return { dx: 0, dy: 0 };
  }
  const step = markerPackStep();
  const span = (count - 1) * step;
  return { dx: index * step - span / 2, dy: 0 };
}
